import React, { useState } from 'react';
import { formatEpochDateTime } from '../../utils/dateUtils';
import { deleteReport, downloadReport } from '../../services/api';
import './ReadyReports.css';

function ReadyReports({ loadingReports, queueReports, onReportClick, onReportDeleted }) {
  const [deletingReports, setDeletingReports] = useState(new Set());
  const [downloadingReports, setDownloadingReports] = useState(new Set());

  // Helper function to convert settings keys to readable section names
  const getSectionNames = (report) => {
    if (!report.settings) return 'Newsletter';
    
    return Object.keys(report.settings)
      .map(key => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
      .join(', ');
  };

  // Helper function to get the first letter of the report name
  const getReportIconLetter = (report) => {
    const reportName = report.title || getSectionNames(report);
    return reportName.charAt(0).toUpperCase();
  };

  // Combine all reports and sort by timestamp (newest first)
  const sortedReports = [...queueReports].sort((a, b) => {
    const timestampA = a.timestamp || a.date || 0;
    const timestampB = b.timestamp || b.date || 0;
    return timestampB - timestampA; // Newest first
  });

  const handleReportClick = (report) => {
    if (onReportClick) {
      onReportClick(report);
    }
  };

  const handleViewReport = (report, e) => {
    e.stopPropagation();
    if (onReportClick) {
      onReportClick(report);
    }
  };

  const handleDownloadReport = async (report, e) => {
    e.stopPropagation();
    
    try {
      setDownloadingReports(prev => new Set(prev).add(report.id));
      
      // Use the same ID logic as ReportModal
      const reportId = report.report_id || report.id;
      console.log('Downloading report with ID:', reportId);
      
      // Download the PDF blob
      const pdfBlob = await downloadReport(reportId);
      
      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloadingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(report.id);
        return newSet;
      });
    }
  };

  const handleDeleteReport = async (report, e) => {
    e.stopPropagation();
    

    try {
      setDeletingReports(prev => new Set(prev).add(report.id));
      await deleteReport(report.id);
      
      // Call the callback to refresh the reports list
      if (onReportDeleted) {
        onReportDeleted(report.id);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Failed to delete report. Please try again.');
    } finally {
      setDeletingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(report.id);
        return newSet;
      });
    }
  };

  return (
    <div className="ready-reports-section component-card">
      <h2>REPORT HISTORY</h2>
      {loadingReports ? (
        <p>Loading your reports...</p>
      ) : queueReports.length > 0 ? (
        <div className="reports-list-compact">
          <div className="reports-section">
            <div className="reports-list scrollable-compact">
              {sortedReports.map((report) => (
                <div
                  key={report.id}
                  className={`queue-report-item ${report.status === 'completed' ? 'finished clickable' : 'unfinished'}`}
                  onClick={report.status === 'completed' ? () => handleReportClick(report) : undefined}
                >
                  <div className="report-icon">
                    {getReportIconLetter(report)}
                  </div>
                  <div className="queue-report-info">
                    <h4 className="report-title">{report.title || getSectionNames(report)}</h4>
                    <div className="report-status-row">
                      <span className={`status-indicator ${report.status === 'completed' ? 'ready' : report.status === 'failed' ? 'failed' : 'processing'}`}>•</span>
                      <span className="status-text">
                        {report.status === 'completed' ? 'Ready' : 
                         report.status === 'failed' ? 'Failed' : 'Processing'}
                      </span>
                      <span className="timestamp">{formatEpochDateTime(report.timestamp || report.date)}</span>
                    </div>
                  </div>
                  <div className="report-actions">
                    <button 
                      className={`action-btn view-btn ${report.status === 'completed' ? 'primary' : 'disabled'}`}
                      disabled={report.status !== 'completed'}
                      onClick={(e) => handleViewReport(report, e)}
                    >
                      View
                    </button>
                    <button 
                      className={`action-btn download-btn ${report.status === 'completed' ? '' : 'disabled'} ${downloadingReports.has(report.id) ? 'downloading' : ''}`}
                      disabled={report.status !== 'completed' || downloadingReports.has(report.id)}
                      onClick={(e) => handleDownloadReport(report, e)}
                    >
                      {downloadingReports.has(report.id) ? 'Downloading...' : 'Download'}
                    </button>
                    <button 
                      className={`action-btn delete-btn ${report.status === 'completed' ? '' : 'disabled'} ${deletingReports.has(report.id) ? 'deleting' : ''}`}
                      disabled={report.status !== 'completed' || deletingReports.has(report.id)}
                      onClick={(e) => handleDeleteReport(report, e)}
                    >
                      {deletingReports.has(report.id) ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <p>No reports in queue yet. Generate your first newsletter below!</p>
      )}
    </div>
  );
}

export default ReadyReports;
